/**
 * CSS styles for Synesthesia editor
 */

import { themeToCSSVars } from './themes.js';

export function generateStyles(options = {}) {
  const {
    fontSize = '14px',
    lineHeight = 1.6,
    fontFamily = '"SF Mono", SFMono-Regular, Menlo, Monaco, "Cascadia Code", Consolas, "Roboto Mono", "Noto Sans Mono", "Droid Sans Mono", "Ubuntu Mono", "DejaVu Sans Mono", "Liberation Mono", "Courier New", Courier, monospace',
    padding = '20px',
    theme = null,
    mobile = {
      fontSize: '16px', // Prevent zoom on iOS
      padding: '12px'
    }
  } = options;

  const themeVars = theme && theme.colors ? themeToCSSVars(theme.colors) : '';
  const syntaxStyles = theme && theme.colors ? generateSyntaxStyles(theme.colors) : '';

  return `
    /* Synesthesia Editor Styles */
    .synesthesia-container * {
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      float: none !important;
      clear: none !important;
      text-decoration: none !important;
      text-transform: none !important;
      letter-spacing: normal !important;
      box-shadow: none !important;
      text-shadow: none !important;
      box-sizing: border-box !important;
    }
    
    .synesthesia-container {
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      position: relative !important;
      overflow: hidden !important;
      ${themeVars ? `/* Theme Variables */
${themeVars}` : ''}
    }
    
    .synesthesia-container.synesthesia-auto-resize {
      height: auto !important;
    }
    
    .synesthesia-wrapper {
      position: relative !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 48px !important;
      overflow: hidden !important;
      background: var(--bg-secondary, #ffffff) !important;
    }

    /* Critical alignment styles */
    .synesthesia-wrapper .synesthesia-input,
    .synesthesia-wrapper .synesthesia-preview {
      position: absolute !important;
      top: 0 !important; left: 0 !important;
      width: 100% !important; height: 100% !important;
      font-family: ${fontFamily} !important;
      font-variant-ligatures: none !important;
      font-size: var(--instance-font-size, ${fontSize}) !important;
      line-height: var(--instance-line-height, ${lineHeight}) !important;
      padding: var(--instance-padding, ${padding}) !important;
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      tab-size: 2 !important; -moz-tab-size: 2 !important;
      overflow: auto !important;
      /* All other properties that could affect layout */
      font-weight: normal !important; font-style: normal !important;
      font-variant: normal !important; font-stretch: normal !important;
      font-kerning: none !important; font-feature-settings: normal !important;
      border: none !important; outline: none !important;
      text-align: left !important; text-indent: 0 !important;
      letter-spacing: normal !important; word-spacing: normal !important;
      text-transform: none !important; text-rendering: auto !important;
      -webkit-font-smoothing: auto !important; -webkit-text-size-adjust: 100% !important;
      direction: ltr !important; writing-mode: horizontal-tb !important;
      animation: none !important; transition: none !important;
    }

    .synesthesia-wrapper .synesthesia-input {
      z-index: 1 !important;
      color: transparent !important;
      caret-color: var(--cursor, #f95738) !important;
      background-color: transparent !important;
      resize: none !important;
      appearance: none !important; -webkit-appearance: none !important;
      touch-action: manipulation !important;
      autocomplete: off !important; autocorrect: off !important;
      autocapitalize: off !important; spellcheck: false !important;
    }

    .synesthesia-wrapper .synesthesia-input::selection {
      background-color: var(--selection, rgba(244, 211, 94, 0.4));
    }

    .synesthesia-wrapper .synesthesia-preview {
      z-index: 0 !important;
      pointer-events: none !important;
      color: var(--text, #0d3b66) !important;
      background-color: transparent !important;
      user-select: none !important; -webkit-user-select: none !important;
    }
    
    /* Generic token styles for syntax highlighting */
    .token.comment, .token.prolog, .token.doctype, .token.cdata { color: var(--token-comment); }
    .token.punctuation { color: var(--token-punctuation); }
    .token.property, .token.tag, .token.boolean, .token.number, .token.constant, .token.symbol, .token.deleted { color: var(--token-property); }
    .token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted { color: var(--token-string); }
    .token.operator, .token.entity, .token.url, .language-css .token.string, .style .token.string { color: var(--token-operator); }
    .token.atrule, .token.attr-value, .token.keyword { color: var(--token-keyword); }
    .token.function, .token.class-name { color: var(--token-function); }
    .token.regex, .token.important, .token.variable { color: var(--token-variable); }
    .token.important, .token.bold { font-weight: bold; }
    .token.italic { font-style: italic; }
    .token.entity { cursor: help; }
    
    /* Syntax highlighting classes from parser */
    ${syntaxStyles}
    
    /* Mobile responsiveness - prevent zoom on iOS */
    @media (max-width: 640px) {
      .synesthesia-wrapper .synesthesia-input,
      .synesthesia-wrapper .synesthesia-preview {
        font-size: ${mobile.fontSize} !important;
        padding: ${mobile.padding} !important;
      }
    }
  `;
}

/**
 * Generate syntax highlighting CSS from theme colors
 * @param {object} colors - Theme colors object
 * @returns {string} CSS for syntax classes
 */
function generateSyntaxStyles(colors) {
  const styles = [];
  
  // Generate styles for syn-* classes
  Object.entries(colors).forEach(([key, value]) => {
    if (key.startsWith('syn-')) {
      const className = key.replace('syn-', '');
      styles.push(`
    .synesthesia-preview .syn-${className} {
      color: ${value} !important;
    }`);
    }
  });
  
  // Add some common syntax styles
  styles.push(`
    .synesthesia-preview .syn-comment {
      font-style: italic !important;
      opacity: 0.8 !important;
    }
    
    .synesthesia-preview .syn-keyword {
      font-weight: 500 !important;
    }
    
    .synesthesia-preview .syn-string {
      font-style: normal !important;
    }
    
    .synesthesia-preview .syn-heading {
      font-weight: 700 !important;
      margin-top: 0.5em !important;
      margin-bottom: 0.5em !important;
    }
    
    .synesthesia-preview .syn-function {
      font-weight: 500 !important;
    }
  `);
  
  return styles.join('');
}
